'use client';

import { cn } from '@/lib/utils';
import { cx } from 'cva';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import { BsBellFill, BsGlobeAmericas } from 'react-icons/bs';
import { GoHomeFill } from 'react-icons/go';
import { MdAdminPanelSettings } from 'react-icons/md';
import { FaHeart } from 'react-icons/fa';
import { GoHeartFill } from 'react-icons/go';
import Avatar from '../next-ui/Avatar';
import Typography from '../next-ui/Typography';
import { isCompanyAdmin, isNonProfitAdmin } from '@/utils/communityRoles';
import { kebabCase, last } from 'lodash-es';
import { MdExplore } from 'react-icons/md';
import { getAdminLoginUrl } from '@/utils/stringHelpers';

const getNonProfitLink = (user) => {
  const activeCommunity = user.activeCommunity;
  let slug = activeCommunity.hasPublicProfile
    ? last(activeCommunity.publicProfile.slug?.split('/'))
    : null;

  if (!slug) {
    slug = `${kebabCase(activeCommunity?.name)}-${
      activeCommunity?.nonProfit?.id
    }`;
  }
  return `/organization/${slug}`;
};

const webMenuItems = [
  {
    id: 'home',
    label: 'Home',
    icon: <GoHomeFill className="h-6 w-6" />,
    path: '/home',
    className: 'hidden lg:flex',
  },
  {
    id: 'explore',
    label: 'Explore',
    icon: <MdExplore className="h-6 w-6" />,
    path: '/explore',
    className: 'hidden lg:flex',
  },
  {
    id: 'notifications',
    label: 'To Do',
    icon: <BsBellFill className="h-6 w-6" />,
    path: '/notifications',
    className: 'hidden lg:flex',
  },
];

const nonProfitAdminItems = [
  {
    id: 'home',
    label: 'Home',
    icon: <GoHomeFill className="h-6 w-6" />,
    path: '/home',
    className: 'hidden lg:flex',
  },
  {
    id: 'my-nonprofit',
    label: 'My Nonprofit',
    icon: <FaHeart className="h-6 w-6" />,
    path: '/organization',
    className: 'hidden lg:flex',
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: <MdAdminPanelSettings className="h-6 w-6" />,
    path: process.env.NEXT_PUBLIC_ADMIN_APP_URL,
    className: 'hidden lg:flex',
  },
];

const getMenuItems = (user, avatar, name) => {
  const isNpoAdmin = isNonProfitAdmin(user);
  const isCompAdmin = isCompanyAdmin(user);

  if (isNpoAdmin) {
    return [
      {
        id: 'home',
        label: 'Home',
        icon: <GoHomeFill className="h-6 w-6" />,
        path: '/home',
        className: 'hidden lg:flex',
      },
      // {
      //   id: 'my-nonprofit',
      //   label: 'My Nonprofit',
      //   icon: <GoHeartFill className="h-6 w-6" />,
      //   path: getNonProfitLink(user),
      //   className: 'hidden lg:flex',
      // },
      {
        id: 'explore',
        label: 'Explore',
        icon: <MdExplore className="h-6 w-6" />,
        path: '/explore',
        className: 'hidden lg:flex',
      },
      {
        id: 'administration',
        label: 'Administration',
        icon: <MdAdminPanelSettings className="h-6 w-6" />,
        path: getAdminLoginUrl(user?.email, user?.accessToken),
        className: 'hidden lg:flex',
      },
      {
        id: 'notifications',
        label: 'To Do',
        icon: <BsBellFill className="h-6 w-6" />,
        path: '/notifications',
        className: 'hidden lg:flex',
      },
      {
        id: 'profile-summary',
        // label: 'Profile',
        label: '',
        icon: <Avatar src={avatar} alt={name} className="h-8 w-8" />,
        path: '/profile/summary',
        className: 'flex lg:hidden',
      },
    ];
  }

  if (isCompAdmin) {
    return [
      {
        id: 'home',
        label: 'Home',
        icon: <GoHomeFill className="h-6 w-6" />,
        path: '/home',
        className: 'hidden lg:flex',
      },
      {
        id: 'explore',
        label: 'Explore',
        icon: <MdExplore className="h-6 w-6" />,
        path: '/explore',
        className: 'hidden lg:flex',
      },
      {
        id: 'administration',
        label: 'Administration',
        icon: <MdAdminPanelSettings className="h-6 w-6" />,
        path: getAdminLoginUrl(user?.email, user?.accessToken),
        className: 'hidden lg:flex',
      },
      {
        id: 'notifications',
        label: 'To Do',
        icon: <BsBellFill className="h-6 w-6" />,
        path: '/notifications',
        className: 'hidden lg:flex',
      },
      {
        id: 'profile-summary',
        // label: 'Profile',
        label: '',
        icon: <Avatar src={avatar} alt={name} className="h-8 w-8" />,
        path: '/profile/summary',
        className: 'flex lg:hidden',
      },
    ];
  }

  return [
    ...webMenuItems,
    {
      id: 'profile-summary',
      label: '',
      icon: <Avatar src={avatar} alt={name} className="h-8 w-8" />,
      path: '/profile/summary',
      className: 'flex lg:hidden',
    },
  ];
};

const TabIcon = ({
  icon,
  path,
  isSelected,
  direction,
  label,
  className = '',
  unReadNotifications = 0,
  user,
}) => {
  const isNpoAdmin = isNonProfitAdmin(user);

  return (
    <Link
      passHref
      href={path}
      className={cn(
        'relative flex h-full w-[70px] cursor-pointer flex-col items-center',
        className,
        // label === 'Home' && 'mr-4',
      )}>
      {label === 'To Do' && unReadNotifications > 0 && (
        <div className="absolute right-[3%] top-[2px] z-10 flex h-6 w-6 items-center justify-center rounded-lg bg-red-600 text-11 text-white">
          {unReadNotifications >= 99 ? '99+' : unReadNotifications}
        </div>
      )}
      {/* <div className="flex flex-1 items-center">
        <div
          className={cx(
            'flex h-8 w-8 transform items-center justify-center rounded-full p-1 text-white transition-all duration-1000 ease-in-out',
            isSelected ? 'bg-primary' : 'bg-text-disabled',
          )}>
          {icon}
        </div>
        <Typography
          variant="h6"
          className={cx('text-center font-normal', 'text-text-secondary')}>
          {label}
        </Typography>
      </div> */}

      <div className="flex w-full flex-1 flex-col items-center justify-center">
        <div
          className={cx(
            'flex h-8 w-8 transform items-center justify-center rounded-full p-1 text-white transition-all duration-1000 ease-in-out',
            isSelected ? 'bg-primary' : ' bg-text-disabled',
            // label !== 'Notification' && 'px-2',
          )}>
          {icon}
        </div>
        <Typography
          variant="h6"
          className={cx(
            'text-center text-[10px] font-normal',
            isSelected ? '' : '',
          )}>
          {label}
        </Typography>
      </div>

      <div className="relative h-[2px] w-full">
        <div
          className={cx(
            'duration-250 absolute top-0 h-full transform bg-primary ease-in',
            isSelected ? 'w-full transition-all' : 'w-0 transition-none',
            isSelected && direction === 'right' ? 'left-0' : 'right-0',
          )}></div>
      </div>
    </Link>
  );
};

function AppNavMenu(props) {
  const { user, avatar, name, unReadNotifications } = props;
  const path = usePathname();
  const [selectedTab, setSelectedTab] = useState(null);
  const [direction, setDirection] = useState('right');

  let menuItems = useMemo(() => getMenuItems(user, avatar, name), [user]);

  useEffect(() => {
    const tab = menuItems.find((item) => path === item.path);
    if (tab) {
      setSelectedTab(tab);
    } else {
      setSelectedTab(null);
    }
  }, [path]);

  const handleTabClick = (tab) => {
    // avoid self routing
    if (tab !== selectedTab) {
      const currentIndex = menuItems.findIndex(
        (item) => item.id === selectedTab,
      );
      const nextIndex = menuItems.findIndex((item) => item.id === tab);
      setDirection(nextIndex > currentIndex ? 'right' : 'left');
      setSelectedTab(tab);
    }
  };

  return (
    <div className="flex h-full justify-center">
      <div className="flex h-full items-center justify-between gap-2 lg:gap-6">
        {menuItems.map((item) => (
          <TabIcon
            key={item.id}
            icon={item.icon}
            label={item.label}
            path={item.path}
            isSelected={selectedTab?.id === item.id}
            direction={direction}
            className={item.className}
            onClick={() => handleTabClick(item.id)}
            unReadNotifications={unReadNotifications}
            user={user}
          />
        ))}
      </div>
    </div>
  );
}

export default React.memo(AppNavMenu);
